import type { PayloadAction } from '@reduxjs/toolkit';
import { createAsyncThunk } from '@reduxjs/toolkit';
import axios from 'axios';
import { Buffer } from 'buffer';

import { Services } from 'services';
import { uploadImagesWithPresignedUrls } from 'utils/files';
import { transformErrorResponse, getClientIpAddress } from 'utils';

import { actionMapForOtherLoaders } from 'constants/index';
import { ConsentAgreementKind } from 'constants/common';
import { IFile } from 'models/general';
import { IPositiveDrugTestResult } from 'models/drugTesting';
import { getUpdateWorkerBodyFromOnboarding } from 'utils/onboarding';
import { StepKey } from 'modules/views/shared/Wizard/config';
import initialState, { initialOnboardingTempState } from './state';
import { getImageFormat, getImageMimeType } from 'utils/files';

const setOnboarding = (state, action: PayloadAction<any>) => {
  state = {
    ...(state || {}),
    ...(action.payload || {}),
  };
  return state;
};

const setHasReachedConsentForm = (state, action: PayloadAction<boolean>) => {
  state.temp = {
    ...state.temp,
    hasReachedConsentForm: action.payload,
  };
};

const addVisitedWizardStep = (state, action: PayloadAction<StepKey>) => {
  const visitedWizardSteps = state.temp?.visitedWizardSteps || [];

  if (visitedWizardSteps.includes(action.payload)) {
    return;
  }

  state.temp = {
    ...state.temp,
    visitedWizardSteps: [...visitedWizardSteps, action.payload],
  };
};

const setConsentAcceptance = (state, action: PayloadAction<{ id: string; accepted: boolean }>) => {
  const { id, accepted } = action.payload;
  if (!id) {
    return;
  }
  const current: string[] = state.temp?.consentAcceptances || [];
  const next = accepted
    ? current.includes(id)
      ? current
      : [...current, id]
    : current.filter((existingId: string) => existingId !== id);

  state.temp = {
    ...state.temp,
    consentAcceptances: next,
  };
};

const setTemp = (state, action: PayloadAction<any>) => {
  state.temp = {
    ...(action.payload || {}),
    visitedWizardSteps: action.payload?.visitedWizardSteps ?? state.temp?.visitedWizardSteps ?? [],
  };
};

const addGovernmentDocumentFile = (state, action: PayloadAction<IFile>) => {
  state.governmentDocumentFiles = [...(state.governmentDocumentFiles || []), action.payload];
};

const setWorkerPIIPinInformation = (
  state,
  action: PayloadAction<{ isPIIVisible: boolean; piiFieldVisibilityExpiredAt: string | null }>,
) => {
  state.workerPIIPinInformation = action.payload;
  state.updatedAt = new Date();
};

const setDbMetadata = (state, action: PayloadAction<{ id: string; projectId: string }>) => {
  state.db = action.payload;
};

const resetOnboardingState = (state, action: PayloadAction<{ resetTemp: boolean }>) => {
  // const tempData =
  state = { ...initialState, temp: action?.payload?.resetTemp ? initialOnboardingTempState : state.temp };
  return state;
};

const resetTempOnboardingData = state => {
  state.temp = initialOnboardingTempState;
};

/**
 * Builds assignment request parameters for assigning a worker to companies.
 * Filters out already-assigned companies and builds inviteMethodValue based on inviteMethod.
 * Returns null if there is nothing to assign.
 */
const buildAssignmentParams = ({
  workerId,
  employers,
  assignedCompanies,
  inviteMethod,
  email,
  mobilePhoneNumber,
}: {
  workerId: string;
  employers: { id: string; name: string }[];
  assignedCompanies: { id: string }[];
  inviteMethod: number;
  email?: string;
  mobilePhoneNumber?: string;
}): { assignRequestData: any[]; assignedDataToAdd: any[] } | null => {
  const notAssignedCompanies = employers.filter(
    company => !assignedCompanies.some(assigned => assigned.id === company.id),
  );

  if (notAssignedCompanies.length === 0) return null;

  let inviteMethodValue = {};
  if (inviteMethod === 1) inviteMethodValue = { email: email || '' };
  if (inviteMethod === 2) inviteMethodValue = { mobilePhoneNumber: mobilePhoneNumber || '' };
  if (inviteMethod === 3) inviteMethodValue = { email: email || '', mobilePhoneNumber: mobilePhoneNumber || '' };

  const assignRequestData = notAssignedCompanies.map(company => ({
    id: workerId,
    companyId: company.id,
    inviteMethod,
    ...inviteMethodValue,
  }));

  const assignedDataToAdd = notAssignedCompanies.map(company => ({
    id: company.id,
    name: company.name,
    workerProjectStatus: 0,
  }));

  return { assignRequestData, assignedDataToAdd };
};

const createWorker = createAsyncThunk(
  actionMapForOtherLoaders.createWorker,
  async (
    query: {
      firstName?: string;
      lastName?: string;
      email?: string;
      mobilePhoneNumber?: string;
      inviteMethod: number;
      isAddingFromTablet: boolean;
      companyIds: string[];
      activateWorker: boolean;
      dateOfBirth?: string;
      languageCode?: string;
    },
    thunkApi,
  ) => {
    try {
      const response = await Services.postWorker(query, query.languageCode);
      const createdWorker = response.data;

      // For unverified workers (inviteMethod=0), assign to project immediately after creation
      if (query.inviteMethod === 0 && createdWorker?.id && query.companyIds?.length > 0) {
        const state = thunkApi.getState() as any;
        const projectId = state?.onboarding?.temp?.project?.value || state?.cache?.project?.id;

        if (projectId) {
          const params = buildAssignmentParams({
            workerId: createdWorker.id,
            employers: query.companyIds.map(companyId => ({ id: companyId, name: '' })),
            assignedCompanies: [],
            inviteMethod: query.inviteMethod,
          });

          if (params) {
            await thunkApi.dispatch(
              assignWorkerToCompanies({
                projectId,
                assignRequestData: params.assignRequestData,
                assignedDataToAdd: params.assignedDataToAdd,
                languageCode: query.languageCode,
              }),
            );
          }
        }
      }

      return createdWorker;
    } catch (error) {
      return thunkApi.rejectWithValue(transformErrorResponse(error));
    }
  },
);

const updateWorker = createAsyncThunk(
  actionMapForOtherLoaders.updateWorker,
  async (
    query: {
      workerId: string;
      data: any;
      languageCode?: any;
      workerCustomFieldCodesToValidate?: string[];
    },
    thunkApi,
  ) => {
    try {
      const state = thunkApi.getState() as any;
      const onboardingCurrentData = state?.onboarding || {};
      const skilledTrades = state?.general?.skilledTrades[query.languageCode] || [];

      // Build request body
      const requestBody = {
        ...getUpdateWorkerBodyFromOnboarding(onboardingCurrentData, { skilledTrades }),
        ...query.data,
      };

      // Add custom field validation codes if provided
      if (query.workerCustomFieldCodesToValidate?.length > 0) {
        requestBody.workerCustomFieldCodesToValidate = query.workerCustomFieldCodesToValidate;
      }

      // Update worker
      const response = await Services.updateWorker(query.workerId, requestBody, query.languageCode);

      return response.data;
    } catch (error: any) {
      return thunkApi.rejectWithValue(error?.response?.data?.errors || error?.response?.data || error.message);
    }
  },
);

const searchWorker = createAsyncThunk(
  actionMapForOtherLoaders.searchWorker,
  async (
    query: {
      firstName: string;
      lastName: string;
      email?: string;
      mobilePhoneNumber?: string;
      dateOfBirth?: string;
      languageCode?: string;
      pageNumber?: number;
      pageSize?: number;
    },
    thunkApi,
  ) => {
    let queryString = '';
    if (query.firstName) queryString += `FirstName=${encodeURIComponent(query.firstName)}&`;
    if (query.lastName) queryString += `LastName=${encodeURIComponent(query.lastName)}&`;
    if (query.email) queryString += `Email=${encodeURIComponent(query.email)}&`;
    if (query.mobilePhoneNumber) queryString += `MobileNumber=${encodeURIComponent(query.mobilePhoneNumber)}&`;
    if (query.dateOfBirth) queryString += `DateOfBirth=${encodeURIComponent(query.dateOfBirth)}&`;
    // Pagination params (PageNumber, PageSize)
    if (query.pageNumber) queryString += `PageNumber=${encodeURIComponent(query.pageNumber)}&`;
    if (query.pageSize) queryString += `PageSize=${encodeURIComponent(query.pageSize)}&`;

    return Services.searchWorker(queryString, query.languageCode)
      .then(response => {
        return {
          items: response.data.items || [],
          totalCount: response.data.totalResults || 0,
        };
      })
      .catch(error => {
        return thunkApi.rejectWithValue(transformErrorResponse(error));
      });
  },
);

const createPassword = createAsyncThunk(
  actionMapForOtherLoaders.createPassword,
  async (
    query: {
      email?: string;
      mobilePhoneNumber?: string;
      inviteMethod: number;
      isFromWorkerOnBoardingApp: boolean;
      languageCode?: string;
      password: string;
    },
    thunkApi,
  ) => {
    try {
      const response = await Services.register(query, query.languageCode);
      return response.data;
    } catch (error) {
      return thunkApi.rejectWithValue(transformErrorResponse(error));
    }
  },
);

const acceptBiometricConsent = createAsyncThunk(
  actionMapForOtherLoaders.acceptBiometricConsent,
  async (_, thunkApi) => {
    const onboardingData = (thunkApi.getState() as any)?.onboarding || {};
    const languageCode = (thunkApi.getState() as any)?.app?.languageCode || 'en';
    return Services.updateWorkerBiometricConsent(onboardingData?.id, { biometricConsent: true }, languageCode)
      .then(response => {
        return { signedBiometricConsentForm: true };
      })
      .catch(error => {
        return thunkApi.rejectWithValue(transformErrorResponse(error));
      });
  },
);

const uploadImageToS3 = createAsyncThunk(
  actionMapForOtherLoaders.uploadImageToS3,
  async (
    {
      apiFunction,
      apiFunctionArgs,
      image,
      languageCode,
    }: {
      apiFunction: (...params: any) => Promise<any>;
      apiFunctionArgs: any[];
      image: any;
      languageCode: string;
      customLoadingKey: string;
    },
    thunkApi,
  ) => {
    try {
      // Step 1: Get presigned URL from API
      const response = await apiFunction(...apiFunctionArgs);

      if (!response.data?.url) {
        // API did not return URL, throw error to catch block
        throw response.data?.errors;
      }

      // Step 2: Upload image to S3/Blob using the signed URL
      const imageFormat = getImageFormat(image);
      const contentType = getImageMimeType(imageFormat);
      const config = {
        method: 'put',
        url: response.data.url,
        headers: {
          'Content-Type': contentType,
        },
        data: Buffer.from(image.replace(/^data:image\/\w+;base64,/, ''), 'base64'),
      };

      const s3Response = await axios(config);

      if (s3Response.status !== 200) {
        // Upload failed, throw error to catch block
        throw response.data?.errors;
      }

      // Step 3: Confirm upload with backend
      const confirmationResponse = await Services.confirmImageUpload(response.data.fileId, languageCode);
      return confirmationResponse.data;
    } catch (error) {
      return thunkApi.rejectWithValue(transformErrorResponse(error));
    }
  },
);

const fetchWorkerDetails = createAsyncThunk(
  actionMapForOtherLoaders.fetchWorkerDetails,
  async (
    query: {
      workerId: string;
      languageCode?: string;
    },
    thunkApi,
  ) => {
    return Services.getWorker(query.workerId, query.languageCode)
      .then(response => {
        return response.data;
      })
      .catch(error => {
        return thunkApi.rejectWithValue(transformErrorResponse(error));
      });
  },
);

const fetchWorkerExistingConsentForms = createAsyncThunk(
  actionMapForOtherLoaders.fetchWorkerExistingConsentForms,
  async (
    query: {
      workerId: string;
      projectId: string;
      languageCode?: string;
    },
    thunkApi,
  ) => {
    return Services.getWorkerExistingConsentForms(query.workerId, query.projectId, query.languageCode)
      .then(response => {
        return response.data;
      })
      .catch(error => {
        return thunkApi.rejectWithValue(transformErrorResponse(error));
      });
  },
);

const uploadGovernmentDocumentFiles = createAsyncThunk(
  actionMapForOtherLoaders.uploadGovernmentDocumentFiles,
  async (_, thunkApi) => {
    try {
      const store = thunkApi.getState() as any;
      const governmentDocumentFiles = store?.onboarding?.governmentDocumentFiles?.filter(file => file.tempId) || [];
      const workerId = store?.onboarding?.id;
      const languageCode = store?.app?.languageCode || 'en';
      const results = await uploadImagesWithPresignedUrls({
        apiFunction: Services.uploadGovernmentDocuments,
        apiFunctionArgs: [
          workerId,
          { fileNames: governmentDocumentFiles.map((file: IFile) => file.fileName) },
          languageCode,
        ],
        images: governmentDocumentFiles,
        languageCode,
      });

      return results;
    } catch (error) {
      return thunkApi.rejectWithValue(transformErrorResponse(error));
    }
  },
);

const deleteGovernmentDocumentFile = createAsyncThunk(
  actionMapForOtherLoaders.deleteGovernmentDocumentFile,
  async (
    query: {
      workerId: string;
      fileId?: string;
      tempId?: string;
      languageCode?: string;
    },
    thunkApi,
  ) => {
    if (query.tempId) {
      const governmentDocumentFiles = (thunkApi.getState() as any)?.onboarding?.governmentDocumentFiles || [];
      return governmentDocumentFiles.filter((file: IFile) => file.tempId !== query.tempId);
    }
    return Services.deleteGovernmentDocument(query.workerId, query.fileId, query.languageCode)
      .then(response => {
        const governmentDocumentFiles = (thunkApi.getState() as any)?.onboarding?.governmentDocumentFiles || [];
        return governmentDocumentFiles.filter((file: IFile) => file.id !== query.fileId);
      })
      .catch(error => {
        return thunkApi.rejectWithValue(transformErrorResponse(error));
      });
  },
);

const fetchCertificationGroups = createAsyncThunk(
  actionMapForOtherLoaders.fetchCertificationGroups,
  async (query: { projectId: string; workerId: string; languageCode?: string }, thunkApi) => {
    return Services.fetchCertificationGroups(query.projectId, query.workerId, query.languageCode || 'en')
      .then(response => {
        return response.data || [];
      })
      .catch(error => {
        return thunkApi.rejectWithValue(transformErrorResponse(error));
      });
  },
);

const fetchAllCertifications = createAsyncThunk(
  actionMapForOtherLoaders.fetchAllCertifications,
  async (query: { workerId: string; languageCode?: string }, thunkApi) => {
    const state = thunkApi.getState() as any;
    const isWorker = state.user.isWorker || false;

    return Services.getAllCertifications(query.workerId, query.languageCode || 'en', isWorker)
      .then(response => {
        return response.data || [];
      })
      .catch(error => {
        return thunkApi.rejectWithValue(transformErrorResponse(error));
      });
  },
);

const addCertification = createAsyncThunk(
  actionMapForOtherLoaders.addCertification,
  async (
    query: {
      workerId: string;
      certificationData: {
        certificationId: string;
        idNumber?: string;
        description?: string;
        completionDate?: string;
        expirationDate?: string;
        projectId?: string | null;
      };
      images?: Array<{ tempId?: string; image: string; fileName?: string }>;
      languageCode?: string;
    },
    thunkApi,
  ) => {
    const state = thunkApi.getState() as any;
    const isWorker = state.user.isWorker || false;

    return Services.addCertification(query.workerId, query.certificationData, query.languageCode || 'en', isWorker)
      .then(async addCertificationResponse => {
        // Get the created certification ID from response
        const createdCertificationId = addCertificationResponse.data?.id;

        // If there are images and we have a certification ID, upload them
        if (query.images && query.images.length > 0 && createdCertificationId) {
          try {
            // Upload images using the same pattern as government documents
            await uploadImagesWithPresignedUrls({
              apiFunction: Services.uploadCertificate,
              apiFunctionArgs: [
                query.workerId,
                createdCertificationId,
                {
                  fileNames: query.images.map(img => {
                    if (img.fileName) return img.fileName;
                    const imageFormat = getImageFormat(img.image);
                    return `${img.tempId}.${imageFormat}`;
                  }),
                },
                query.languageCode || 'en',
              ],
              images: query.images,
              languageCode: query.languageCode || 'en',
            });
          } catch (imageUploadError) {
            return thunkApi.rejectWithValue(transformErrorResponse(imageUploadError));
            // Don't fail the entire operation if image upload fails
          }
        }

        // Fetch all certifications to get updated list
        const certificationsResponse = await Services.getAllCertifications(query.workerId, query.languageCode || 'en');
        return certificationsResponse.data || [];
      })
      .catch(error => {
        return thunkApi.rejectWithValue(transformErrorResponse(error));
      });
  },
);

const assignWorkerToCompanies = createAsyncThunk(
  actionMapForOtherLoaders.assignWorkerToCompanies,
  async (
    query: { projectId: string; assignRequestData: any[]; assignedDataToAdd: any[]; languageCode?: string },
    thunkApi,
  ) => {
    try {
      await Services.assignWorkerToProject(query.projectId, query.assignRequestData, query.languageCode || 'en');
      return { newAssignedCompanies: query.assignedDataToAdd };
    } catch (error) {
      return thunkApi.rejectWithValue(transformErrorResponse(error));
    }
  },
);

/**
 * Assigns an existing worker to new companies within a project.
 * Used for the addEmployerFlow when a worker already exists.
 */
const assignExistingWorkerToCompanies = createAsyncThunk(
  actionMapForOtherLoaders.assignExistingWorkerToCompanies,
  async (
    query: { projectId: string; employers: { value: string; label: string }[]; languageCode?: string },
    thunkApi,
  ) => {
    const state = thunkApi.getState() as any;
    const { onboarding } = state;
    const workerId = onboarding?.id;

    if (!workerId || !query.employers?.length) return null;

    const params = buildAssignmentParams({
      workerId,
      employers: query.employers.map(e => ({ id: e.value, name: e.label })),
      assignedCompanies: onboarding?.assignedCompanies || [],
      inviteMethod: onboarding.inviteMethod,
      email: onboarding.email,
      mobilePhoneNumber: onboarding.mobilePhoneNumber,
    });

    if (params) {
      await thunkApi.dispatch(
        assignWorkerToCompanies({
          projectId: query.projectId,
          assignRequestData: params.assignRequestData,
          assignedDataToAdd: params.assignedDataToAdd,
          languageCode: query.languageCode,
        }),
      );
    }

    return null;
  },
);

const fetchAssignedCompanies = createAsyncThunk(
  actionMapForOtherLoaders.fetchAssignedCompanies,
  async (query: { workerId: string; projectId: string; languageCode?: string }, thunkApi) => {
    return Services.getWorkerProjectCompanies(query.workerId, query.projectId, query.languageCode || 'en')
      .then(response => {
        return response.data || [];
      })
      .catch(error => {
        return thunkApi.rejectWithValue(transformErrorResponse(error));
      });
  },
);

const deleteCertification = createAsyncThunk(
  actionMapForOtherLoaders.deleteCertification,
  async (query: { workerId: string; certificateId: string; languageCode?: string }, thunkApi) => {
    return Services.deleteCertification(query.workerId, query.certificateId, query.languageCode)
      .then(async () => {
        // Fetch all certifications to get updated list
        const certificationsResponse = await Services.getAllCertifications(query.workerId, query.languageCode || 'en');
        return certificationsResponse.data || [];
      })
      .catch(error => {
        return thunkApi.rejectWithValue(transformErrorResponse(error));
      });
  },
);

const getCertificationDetails = createAsyncThunk(
  actionMapForOtherLoaders.getCertificationDetails,
  async (
    query: {
      workerId: string;
      certificationId: string;
      languageCode?: string;
    },
    thunkApi,
  ) => {
    return Services.getCertification(query.workerId, query.certificationId, query.languageCode || 'en')
      .then(response => {
        return response.data;
      })
      .catch(error => {
        return thunkApi.rejectWithValue(transformErrorResponse(error));
      });
  },
);

const updateCertification = createAsyncThunk(
  actionMapForOtherLoaders.updateCertification,
  async (
    query: {
      workerId: string;
      certificationId: string;
      certificationData: {
        certificationId: string;
        idNumber?: string;
        description?: string;
        completionDate?: string;
        expirationDate?: string;
        projectId?: string | null;
      };
      images?: Array<{ tempId?: string; image: string; fileName?: string }>;
      deletedFileIds?: string[];
      languageCode?: string;
    },
    thunkApi,
  ) => {
    return Services.updateCertification(
      query.workerId,
      query.certificationId,
      query.certificationData,
      query.languageCode || 'en',
    )
      .then(async updateResponse => {
        // Step 1: Delete files if any are marked for deletion
        if (query.deletedFileIds && query.deletedFileIds.length > 0) {
          try {
            await Services.deleteCertificationFiles(
              query.workerId,
              query.certificationId,
              {
                workerId: query.workerId,
                workerCertificationId: query.certificationId,
                fileIds: query.deletedFileIds,
              },
              query.languageCode || 'en',
            );
          } catch (deleteError) {
            return thunkApi.rejectWithValue(transformErrorResponse(deleteError));
          }
        }

        // Step 2: Upload new images if any exist
        if (query.images && query.images.length > 0) {
          try {
            // Upload images using the same pattern as government documents
            await uploadImagesWithPresignedUrls({
              apiFunction: Services.uploadCertificate,
              apiFunctionArgs: [
                query.workerId,
                query.certificationId,
                { fileNames: query.images.map(img => img.fileName || `${img.tempId}.png`) },
                query.languageCode || 'en',
              ],
              images: query.images,
              languageCode: query.languageCode || 'en',
            });
          } catch (imageUploadError) {
            return thunkApi.rejectWithValue(transformErrorResponse(imageUploadError));
            // Don't fail the entire operation if image upload fails
          }
        }

        // Fetch all certifications to get updated list
        const certificationsResponse = await Services.getAllCertifications(query.workerId, query.languageCode || 'en');
        return certificationsResponse.data || [];
      })
      .catch(error => {
        return thunkApi.rejectWithValue(transformErrorResponse(error));
      });
  },
);

const uploadSignatureToS3 = createAsyncThunk(
  actionMapForOtherLoaders.uploadSignatureToS3,
  async (query: { workerId: string; fileName: string; languageCode: string; signData: string }, thunkApi) => {
    try {
      const { workerId, fileName, languageCode, signData } = query || {};
      const response = await Services.uploadSignature(workerId, { fileName: fileName }, languageCode);

      if (!response.data?.url) {
        throw response.data?.errors;
      }

      // Step 2: Upload image to S3/Blob using the signed URL
      const imageFormat = getImageFormat(signData);
      const contentType = getImageMimeType(imageFormat);
      const config = {
        method: 'put',
        url: response.data.url,
        headers: {
          'Content-Type': contentType,
        },
        data: Buffer.from(signData.replace(/^data:image\/\w+;base64,/, ''), 'base64'),
      };

      const s3Response = await axios(config);

      if (s3Response.status !== 200) {
        throw response.data?.errors;
      }

      // Step 3: Confirm upload with backend
      const confirmationResponse = await Services.confirmImageUpload(response.data.fileId, languageCode);
      return confirmationResponse.data;
    } catch (error) {
      return thunkApi.rejectWithValue(transformErrorResponse(error));
    }
  },
);

const fetchWorkerConsentAcceptances = createAsyncThunk(
  actionMapForOtherLoaders.fetchWorkerConsentAcceptances,
  async (query: { workerId: string; projectId?: string; languageCode?: string }, thunkApi) => {
    const { workerId, projectId, languageCode } = query || {};
    return Services.getConsentAgreements({ projectId, workerId, languageCode })
      .then(response => {
        const accepted = (response.data || []).filter((agreement: any) => agreement?.isAccepted);
        // Worker sessions cannot call the project-level `fetchConsentAgreements` (the API rejects it
        // without a workerId), so for them this response is the ONLY source of the agreement
        // documents and is forwarded to the cache slice. Admins keep `cache.consentAgreements` owned
        // solely by `fetchConsentAgreements`, so the documents are omitted for them.
        // Read after the request resolves, so this is live state — not a stale render closure.
        const isWorker = !!(thunkApi.getState() as any)?.user?.isWorker;
        return {
          ...(isWorker ? { agreements: response.data || [] } : {}),
          standard: accepted
            .filter((a: any) => a.kind === ConsentAgreementKind.Standard)
            .map((a: any) => a.consentAgreementContentId),
          custom: accepted
            .filter((a: any) => a.kind === ConsentAgreementKind.Custom)
            .map((a: any) => a.consentAgreementContentId),
        };
      })
      .catch(error => thunkApi.rejectWithValue(transformErrorResponse(error)));
  },
);

const submitConsentAcceptances = createAsyncThunk(
  actionMapForOtherLoaders.submitConsentAcceptances,
  async (
    query: {
      workerId: string;
      projectId?: string;
      languageCode: string;
      consentAgreementContentIds: string[];
      source: string;
    },
    thunkApi,
  ) => {
    const { workerId, projectId, languageCode, consentAgreementContentIds, source } = query || {};
    const ipAddress = await getClientIpAddress();
    return Services.acceptConsentAgreements(
      workerId,
      {
        projectId: projectId,
        source,
        ipAddress,
        consentAgreementContentIds: consentAgreementContentIds,
      },
      languageCode,
    )
      .then(response => response.data)
      .catch(error => thunkApi.rejectWithValue(transformErrorResponse(error)));
  },
);

const acceptCustomConsentForms = createAsyncThunk(
  actionMapForOtherLoaders.acceptCustomConsentForms,
  async (
    query: {
      workerId: string;
      projectId: string;
      languageCode: string;
      consentAgreementContentIds: string[];
      consentAgreementIds: string[];
      signatureUrl: string;
      source: string;
    },
    thunkApi,
  ) => {
    const { workerId, projectId, languageCode, consentAgreementContentIds, consentAgreementIds, signatureUrl, source } =
      query || {};
    const ipAddress = await getClientIpAddress();
    return Services.acceptConsentAgreements(
      workerId,
      {
        projectId,
        source,
        ipAddress,
        consentAgreementContentIds,
        consentAgreementIds,
        signatureUrl,
      },
      languageCode,
    )
      .then(response => response.data)
      .catch(error => thunkApi.rejectWithValue(transformErrorResponse(error)));
  },
);

const acceptProject = createAsyncThunk(
  actionMapForOtherLoaders.acceptProject,
  async (
    query: { workerId: string; projectId: string; languageCode: string; data: any; companyId: string },
    thunkApi,
  ) => {
    return Services.acceptProject(query.workerId, query.projectId, query.companyId, query.data, query.languageCode)
      .then(response => {
        return { ...response.data, companyId: query.companyId };
      })
      .catch(error => {
        return thunkApi.rejectWithValue(transformErrorResponse(error));
      });
  },
);

const sendPinForPiiVerification = createAsyncThunk(
  actionMapForOtherLoaders.sendPinForPiiVerification,
  async (query: { workerId: string; languageCode: string }, thunkApi) => {
    return Services.generatePinForPiiVerification({ workerId: query.workerId }, query.languageCode)
      .then(response => {
        return response.data;
      })
      .catch(error => {
        return thunkApi.rejectWithValue(transformErrorResponse(error));
      });
  },
);

const verifyPiiPin = createAsyncThunk(
  actionMapForOtherLoaders.verifyPiiPin,
  async (query: { workerId: string; languageCode: string; pinCode: string }, thunkApi) => {
    return Services.validatePinForPiiVerification(
      { workerId: query.workerId, pinCode: query.pinCode },
      query.languageCode,
    )
      .then(response => {
        return response.data;
      })
      .catch(error => {
        return thunkApi.rejectWithValue(transformErrorResponse(error));
      });
  },
);

const getWorkerRequirementStatus = createAsyncThunk(
  actionMapForOtherLoaders.getWorkerRequirementStatus,
  async (
    query: {
      workerId: string;
      projectId: string;
      languageCode?: string;
    },
    thunkApi,
  ) => {
    return Services.getWorkerRequirementStatus(query.workerId, query.projectId, query.languageCode || 'en')
      .then(response => {
        return response.data;
      })
      .catch(error => {
        return thunkApi.rejectWithValue(transformErrorResponse(error));
      });
  },
);

const getRevokeBadgeInfo = createAsyncThunk(
  actionMapForOtherLoaders.getRevokeBadgeInfo,
  async (
    query: {
      workerId: string;
      projectId: string;
      languageCode?: string;
    },
    thunkApi,
  ) => {
    return Services.getRevokeBadgeInfo(query.workerId, query.projectId, query.languageCode || 'en')
      .then(response => {
        return response.data;
      })
      .catch(error => {
        return thunkApi.rejectWithValue(transformErrorResponse(error));
      });
  },
);

const getPositiveDrugTestResults = createAsyncThunk(
  actionMapForOtherLoaders.getPositiveDrugTestResults,
  async (
    query: {
      workerId: string;
      projectId: string;
      companyIds: string[];
      languageCode?: string;
    },
    thunkApi,
  ) => {
    return Services.getPositiveDrugTestResults(
      query.workerId,
      query.projectId,
      query.companyIds,
      query.languageCode || 'en',
    )
      .then(response => {
        return response.data as IPositiveDrugTestResult[];
      })
      .catch(error => {
        return thunkApi.rejectWithValue(transformErrorResponse(error));
      });
  },
);

export {
  setOnboarding,
  setHasReachedConsentForm,
  addVisitedWizardStep,
  setConsentAcceptance,
  setTemp,
  addGovernmentDocumentFile,
  setWorkerPIIPinInformation,
  setDbMetadata,
  resetOnboardingState,
  resetTempOnboardingData,
  createWorker,
  updateWorker,
  searchWorker,
  createPassword,
  acceptBiometricConsent,
  // postWorker,
  uploadImageToS3,
  fetchWorkerDetails,
  fetchWorkerExistingConsentForms,
  uploadGovernmentDocumentFiles,
  deleteGovernmentDocumentFile,
  fetchCertificationGroups,
  fetchAllCertifications,
  addCertification,
  getCertificationDetails,
  updateCertification,
  assignWorkerToCompanies,
  assignExistingWorkerToCompanies,
  fetchAssignedCompanies,
  deleteCertification,
  uploadSignatureToS3,
  submitConsentAcceptances,
  fetchWorkerConsentAcceptances,
  acceptCustomConsentForms,
  acceptProject,
  sendPinForPiiVerification,
  verifyPiiPin,
  getWorkerRequirementStatus,
  getRevokeBadgeInfo,
  getPositiveDrugTestResults,
};
